import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

const apiUrlEmpresas = `${process.env.REACT_APP_API_URL}/empresas`;

function EditEmpresa() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [formData, setFormData] = useState({
        nombre: '',
        razon_social: '',
        cuit: '',
        telefono: '',
        email: '',
        activo: 1
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        getEmpresa();
    }, []);

    const getEmpresa = async () => {
        try {
            const res = await axios.get(`${apiUrlEmpresas}/${id}`);
            setFormData(res.data);
        } catch (error) {
            console.error("Error al obtener empresa:", error);
            alert('❌ Error al cargar la empresa');
            navigate('/admin/AbmEmpresas');
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.nombre.trim()) {
            alert('El nombre de la empresa es requerido');
            return;
        }

        setIsSubmitting(true);
        try {
            await axios.put(`${apiUrlEmpresas}/${id}`, formData);
            alert('✅ Empresa actualizada exitosamente');
            navigate('/admin/AbmEmpresas');
        } catch (error) {
            console.error("Error al actualizar empresa:", error);
            alert('❌ Error al actualizar la empresa: ' + (error.response?.data?.message || error.message));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate('/admin/AbmEmpresas');
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="bg-white p-8 rounded-2xl shadow-xl">
                    <div className="flex items-center gap-4">
                        <svg className="animate-spin h-8 w-8 text-purple-600" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span className="text-lg font-semibold text-gray-700">Cargando datos de la empresa...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 py-8 px-4">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-t-2xl shadow-xl p-6 border-b-4 border-indigo-500">
                    <div className="flex items-center gap-4">
                        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-4 rounded-xl shadow-lg">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Editar Empresa</h1>
                            <p className="text-gray-600 mt-1">Modifica los datos de la empresa <span className="font-semibold text-indigo-600">{formData.nombre}</span></p>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-b-2xl shadow-xl p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Nombre */}
                        <div className="md:col-span-2">
                            <label htmlFor="nombre" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                <span className="text-purple-600">🏢</span>
                                Nombre de la Empresa
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="nombre"
                                name="nombre"
                                value={formData.nombre}
                                onChange={handleChange}
                                placeholder="Ej: Tech Solutions S.A."
                                required
                                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all outline-none text-gray-800 placeholder-gray-400"
                            />
                        </div>

                        {/* Razón Social */}
                        <div className="md:col-span-2">
                            <label htmlFor="razon_social" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                <span className="text-blue-600">📋</span>
                                Razón Social
                            </label>
                            <input
                                type="text"
                                id="razon_social"
                                name="razon_social"
                                value={formData.razon_social || ''}
                                onChange={handleChange}
                                placeholder="Razón social completa"
                                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none text-gray-800 placeholder-gray-400"
                            />
                        </div>

                        {/* CUIT */}
                        <div>
                            <label htmlFor="cuit" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                <span className="text-green-600">🆔</span>
                                CUIT
                            </label>
                            <input
                                type="text"
                                id="cuit"
                                name="cuit"
                                value={formData.cuit || ''}
                                onChange={handleChange}
                                placeholder="20-12345678-9"
                                maxLength={13}
                                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none text-gray-800 placeholder-gray-400"
                            />
                        </div>

                        {/* Teléfono */}
                        <div>
                            <label htmlFor="telefono" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                <span className="text-indigo-600">📞</span>
                                Teléfono
                            </label>
                            <input
                                type="text"
                                id="telefono"
                                name="telefono"
                                value={formData.telefono || ''}
                                onChange={handleChange}
                                placeholder="(011) 4567-8900"
                                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none text-gray-800 placeholder-gray-400"
                            />
                        </div>

                        {/* Email */}
                        <div className="md:col-span-2">
                            <label htmlFor="email" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                <span className="text-pink-600">📧</span>
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email || ''}
                                onChange={handleChange}
                                placeholder="contacto@empresa.com"
                                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-pink-500 focus:ring-2 focus:ring-pink-200 transition-all outline-none text-gray-800 placeholder-gray-400"
                            />
                        </div>

                        {/* Estado Activo */}
                        <div className="md:col-span-2">
                            <label className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg border-2 border-gray-200 cursor-pointer hover:bg-gray-100 transition-all">
                                <input
                                    type="checkbox"
                                    name="activo"
                                    checked={formData.activo === 1}
                                    onChange={(e) => setFormData(prev => ({ ...prev, activo: e.target.checked ? 1 : 0 }))}
                                    className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                                />
                                <div className="flex-1">
                                    <span className="font-semibold text-gray-700">Empresa activa</span>
                                    <p className="text-sm text-gray-500">Si está desactivada, no aparecerá en los selectores del sistema</p>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Botones */}
                    <div className="flex flex-col sm:flex-row gap-4 mt-8 pt-6 border-t-2 border-gray-200">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:from-indigo-700 hover:to-purple-700 transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        >
                            {isSubmitting ? (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Actualizando...
                                </span>
                            ) : (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Guardar Cambios
                                </span>
                            )}
                        </button>

                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 px-6 rounded-lg shadow-md transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="flex items-center justify-center gap-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Cancelar
                            </span>
                        </button>
                    </div>
                </form>

                {/* Info adicional */}
                <div className="mt-6 bg-indigo-50 border-l-4 border-indigo-500 p-4 rounded-r-lg shadow-md">
                    <div className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <p className="text-sm font-semibold text-indigo-900">Ten en cuenta</p>
                            <p className="text-sm text-indigo-700 mt-1">
                                Los cambios en el nombre de la empresa se reflejarán en todo el sistema. Si desactivas la empresa, sus sedes y usuarios seguirán existiendo pero la empresa no aparecerá en los selectores.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EditEmpresa;
